const app = () => {
    if (!window.WebApp)
        throw new Error('MAX Bridge is unavailable');
    return window.WebApp;
};
const unsafeData = () => app().initDataUnsafe ?? {};
export const maxInviteBotUrl = (referralUrl) => {
    const url = new URL(referralUrl);
    url.searchParams.set('start', 'invite');
    url.searchParams.delete('startapp');
    return url.toString();
};
export const maxStartParameter = (unsafe, search) => {
    if (unsafe?.start_param)
        return unsafe.start_param;
    const params = new URLSearchParams(search);
    return (params.get('WebAppStartParam') ?? params.get('start_param') ?? params.get('startapp') ?? null);
};
const signedUser = () => {
    const encoded = new URLSearchParams(app().initData).get('user');
    if (!encoded)
        return undefined;
    try {
        const value = JSON.parse(encoded);
        if (!value || typeof value !== 'object' || Array.isArray(value))
            return undefined;
        const user = value;
        return typeof user.id === 'number' && typeof user.first_name === 'string'
            ? user
            : undefined;
    }
    catch {
        return undefined;
    }
};
export class MaxPlatformAdapter {
    platform = 'max';
    async initialize() {
        if (!window.WebApp) {
            await new Promise((resolve, reject) => {
                const started = Date.now();
                const timer = globalThis.setInterval(() => {
                    if (window.WebApp) {
                        globalThis.clearInterval(timer);
                        resolve();
                    }
                    else if (Date.now() - started >= 8_000) {
                        globalThis.clearInterval(timer);
                        reject(new Error('MAX Bridge is unavailable'));
                    }
                }, 50);
            });
        }
        // MAX documents closing confirmation, but no swipe-disable/expand API.
        // This one-way optional request must never prevent the game from starting.
        try {
            void Promise.resolve(app().enableClosingConfirmation?.()).catch(() => undefined);
        }
        catch {
            // Older native clients can expose an unsupported bridge method.
        }
    }
    async getLaunchData() {
        return {
            raw: app().initData,
            startParameter: maxStartParameter(unsafeData(), window.location.search),
            source: 'max',
        };
    }
    async getCurrentUser() {
        // This object is display-only. The raw initData is always revalidated by the API.
        const user = unsafeData().user ?? signedUser();
        if (!user)
            throw new Error('MAX user data is unavailable');
        return {
            id: String(user.id),
            firstName: user.first_name,
            ...(user.last_name ? { lastName: user.last_name } : {}),
            ...(user.photo_url ? { avatarUrl: user.photo_url } : {}),
            platform: 'max',
        };
    }
    getStartParameter() {
        return maxStartParameter(unsafeData(), window.location.search);
    }
    async share(payload) {
        if (payload.mode === 'invite' && !payload.preserveContext) {
            await this.openExternal(maxInviteBotUrl(payload.url));
            return { shared: true, method: 'native' };
        }
        try {
            await app().shareMaxContent({ text: payload.text, link: payload.url });
            return { shared: true, method: 'native' };
        }
        catch {
            if (!payload.preserveContext && app().openMaxLink) {
                const shareUrl = new URL('https://max.ru/:share');
                shareUrl.searchParams.set('text', `${payload.text}\n${payload.url}`);
                try {
                    await app().openMaxLink(shareUrl.toString());
                    return { shared: true, method: 'native' };
                }
                catch {
                    // Fall back to a copyable invitation below.
                }
            }
            await navigator.clipboard.writeText(`${payload.text}\n${payload.url}`);
            return { shared: false, method: 'clipboard' };
        }
    }
    async openExternal(url) {
        if (url.startsWith('https://max.ru/') && app().openMaxLink)
            await app().openMaxLink(url);
        else if (app().openLink)
            await app().openLink(url);
        else
            window.open(url, '_blank', 'noopener,noreferrer');
    }
    async joinCommunity(url) {
        await this.openExternal(url);
        return true;
    }
    async requestNotifications() {
        // Starting the MAX bot establishes the user-to-bot dialog used for opted-in messages.
        return true;
    }
    async haptic(type) {
        const feedback = app().HapticFeedback;
        if (!feedback)
            return;
        if (type === 'success' || type === 'error')
            feedback.notificationOccurred(type);
        else
            feedback.impactOccurred(type);
    }
    showBackButton() {
        app().BackButton.show();
    }
    hideBackButton() {
        app().BackButton.hide();
    }
    onBack(callback) {
        app().BackButton.onClick(callback);
        return () => app().BackButton.offClick(callback);
    }
    async getViewport() {
        const viewport = await app().getViewportSize();
        return { width: Number(viewport.width), height: Number(viewport.height), stable: false };
    }
    async purchaseProduct() {
        return { completed: false };
    }
}
